/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { Form, Field as FormField, getInput, useForm } from "@formisch/react"
import clsx from "clsx"
import { InfoIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useEffect, useId, useState } from "react"
import * as v from "valibot"

import { SessionViewModel } from "../../features/session/session-view-model"
import { TotpCodeSchema } from "../../features/user/totp-schema"
import { UserTotpViewModel } from "../../features/user/user-totp-viewmodel"
import { LoggerViewModel } from "../../logger/logger-viewmodel"
import { isFieldInvalid } from "../../utils/is-field-invalid"
import { useViewModel } from "../app/app-hooks"
import { BusyButton } from "../components/busy-button"
import { Alert, AlertDescription, AlertTitle } from "../primitives/alert"
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "../primitives/card"
import { Collapsible, CollapsibleContent } from "../primitives/collapsible"
import { Field, FieldError, FieldGroup, FieldLabel } from "../primitives/field"
import { Input } from "../primitives/input"
import { Separator } from "../primitives/separator"
import { Skeleton } from "../primitives/skeleton"
import { Switch } from "../primitives/switch"

import styles from "./totp-setup.module.css"

/**
 * Lets the signed-in user enable or disable TOTP.
 *
 * Enabling generates a new QR code and asks for an authenticator code to confirm
 * it. Disabling asks for the current authenticator code.
 *
 * `flow` tracks the action currently being configured rather than deriving it
 * from the user's saved TOTP state. This keeps the open flow stable while the
 * user data is being refreshed.
 */
export const TotpSetup = observer(() => {
    const [flow, setFlow] = useState<"enable" | "disable">()

    const switchId = useId()

    // Each flow needs its own ID because the old panel can remain mounted during
    // its exit animation while the other panel starts opening.
    const enableFlowId = useId()
    const disableFlowId = useId()

    const sessionVM = useViewModel(SessionViewModel)
    const totpVM = useViewModel(UserTotpViewModel)
    const loggerVM = useViewModel(LoggerViewModel)

    const isEnabled = sessionVM.currentUser?.totpEnabled ?? false

    // Generate a new secret each time the enable flow is opened.
    const toggleEnable = async () => {
        if (flow === "enable") {
            setFlow(undefined)
            return
        }

        setFlow("enable")
        const res = await totpVM.generateTotp()
        if (!res.success) {
            loggerVM.logErrorMessage("Failed to generate token.")
            // The flow cannot proceed without a generated secret.
            setFlow(undefined)
        }
    }

    const toggleDisable = () => setFlow(flow === "disable" ? undefined : "disable")

    const enable = async (code: string) => {
        const res = await totpVM.confirmTotp(code)
        if (res.success) {
            // Refresh the user because enabling TOTP changes its persisted state.
            await sessionVM.getCurrentUser()
            loggerVM.logOk("TOTP enabled successfully.")
            setFlow(undefined)
        } else {
            loggerVM.logErrorMessage("Failed to confirm TOTP.")
        }
    }

    const disable = async (code: string) => {
        const res = await totpVM.disableTotp(code)
        if (res.success) {
            await sessionVM.getCurrentUser()
            loggerVM.logOk("TOTP disabled successfully.")
            setFlow(undefined)
        } else {
            loggerVM.logErrorMessage("Failed to disable TOTP.")
        }
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <h2 className="m-0 text-xl font-bold uppercase">
                        {/* Label the switch so the title is clickable and provides its accessible name. */}
                        <label htmlFor={switchId} className="cursor-pointer select-none">
                            Two-Factor Authentication (TOTP)
                        </label>
                    </h2>
                </CardTitle>
                <CardDescription>Secure your account with a time-based one-time password.</CardDescription>
                <CardAction>
                    <Switch
                        id={switchId}
                        // While configuring a flow, reflect the state being configured
                        // rather than the user's last persisted state.
                        checked={flow ? flow === "enable" : isEnabled}
                        aria-controls={isEnabled ? disableFlowId : enableFlowId}
                        onCheckedChange={() => (isEnabled ? toggleDisable() : toggleEnable())}
                    />
                </CardAction>
            </CardHeader>
            <CardContent className="gap-0">
                <Collapsible open={flow === "enable"}>
                    <CollapsibleContent id={enableFlowId} className={clsx(styles.flow, "grid gap-4")}>
                        <Separator />
                        <QrCode />
                        <TotpCodeForm label="TOTP Code" submitLabel="Enable TOTP" onSubmit={enable} />
                    </CollapsibleContent>
                </Collapsible>

                <Collapsible open={flow === "disable"}>
                    <CollapsibleContent id={disableFlowId} className={clsx(styles.flow, "grid gap-4")}>
                        <Separator />
                        <TotpCodeForm
                            label="Current TOTP Code"
                            submitLabel="Disable TOTP"
                            destructive
                            onSubmit={disable}
                        />
                    </CollapsibleContent>
                </Collapsible>
            </CardContent>
        </Card>
    )
})

/**
 * Shows the TOTP QR code and the authenticator apps known to support SHA-256.
 *
 * The QR code contains the TOTP secret, so clear it when the setup flow ends or
 * the user leaves the page. `generateTotp` clears the previous code before each
 * request, so StrictMode's extra cleanup on mount is harmless.
 */
const QrCode = observer(() => {
    const totpVM = useViewModel(UserTotpViewModel)
    const url = totpVM.totpImageUrl

    // Clear the QR code and its secret when the setup flow unmounts.
    useEffect(() => () => totpVM.clearTotpCode(), [totpVM])

    return (
        <>
            <p className="m-0 text-center text-base font-medium">Scan the QR code with your authenticator app:</p>
            {url ? (
                <img
                    src={url}
                    alt="TOTP QR Code"
                    className="mx-auto size-60 animate-in border animation-duration-800 fade-in"
                />
            ) : (
                <Skeleton className="mx-auto size-60" />
            )}
            <Alert className="border-sky-500/10 bg-sky-500/5 *:[svg]:text-sky-500">
                <InfoIcon />
                <AlertTitle>SHA-256 required</AlertTitle>
                <AlertDescription>
                    Use an authenticator app that supports SHA-256, such as <b>Google Authenticator</b>,{" "}
                    <b>Aegis Authenticator</b>, or <b>FreeOTP+</b>.
                </AlertDescription>
            </Alert>
        </>
    )
})

interface TotpCodeFormProps {
    /**
     * Label displayed above the input.
     */
    label: string
    /**
     * Text displayed on the submit button.
     */
    submitLabel: string
    /**
     * Whether the submit action is destructive.
     */
    destructive?: boolean
    /**
     * Called with the validated TOTP code.
     */
    onSubmit: (code: string) => Promise<void>
}

/**
 * Renders a TOTP code field with its submit button.
 *
 * The form is unmounted when its flow closes, so each time it opens the field
 * starts with an empty value.
 */
const TotpCodeForm = ({ label, submitLabel, destructive = false, onSubmit }: TotpCodeFormProps) => {
    const form = useForm({ schema: TotpCodeSchema, validate: "blur" })
    const id = useId()

    return (
        <Form of={form} onSubmit={({ code }) => onSubmit(code)}>
            <FieldGroup className="gap-4">
                <FormField of={form} path={["code"]}>
                    {(field) => {
                        const invalid = isFieldInvalid(field)

                        return (
                            <Field data-invalid={invalid}>
                                <FieldLabel htmlFor={id}>{label}</FieldLabel>
                                <Input
                                    {...field.props}
                                    id={id}
                                    type="text"
                                    inputMode="numeric"
                                    value={field.input}
                                    aria-invalid={invalid}
                                    autoComplete="one-time-code"
                                    className="h-10"
                                />
                                {invalid && <FieldError>{field.errors?.[0]}</FieldError>}
                            </Field>
                        )
                    }}
                </FormField>
                <BusyButton
                    type="submit"
                    size="lg"
                    variant={destructive ? "destructive" : "default"}
                    busy={form.isSubmitting}
                    disabled={!v.is(TotpCodeSchema, getInput(form))}
                    className="self-end px-4 font-bold tracking-wide uppercase"
                >
                    {submitLabel}
                </BusyButton>
            </FieldGroup>
        </Form>
    )
}
